import React, { useState } from 'react';
import { CalendarCheckIcon, CheckCircle2Icon, Loader2Icon, PhoneIcon } from 'lucide-react';
import { barberOrder, barbers, business, timeSlots } from '../data/business';
import { useLanguage } from '../contexts/LanguageContext';
import { useBooking } from '../contexts/BookingContext';
type Status = 'idle' | 'sending' | 'sent';
type FormState = {
  name: string;
  phone: string;
  service: string;
  date: string;
  time: string;
  notes: string;
};
const fieldClass = 'w-full rounded-sm border border-white/15 bg-ink px-4 py-3 text-base text-cream placeholder:text-cream/40 transition-colors duration-150 ease-out focus:border-gold focus:outline-none';
const labelClass = 'mb-2 block text-sm font-medium text-cream/80';
export function Booking() {
  const {
    t
  } = useLanguage();
  const {
    barberId,
    setBarberId
  } = useBooking();
  const barberName = barberId ? barbers[barberId].name : null;
  const emptyForm: FormState = {
    name: '',
    phone: '',
    service: '',
    date: '',
    time: timeSlots[0],
    notes: ''
  };
  const [form, setForm] = useState<FormState>(emptyForm);
  const [status, setStatus] = useState<Status>('idle');
  const serviceValue = form.service || t.booking.serviceOptions[0];
  const update = (key: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: event.target.value
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 900);
  };
  const today = new Date().toISOString().slice(0, 10);
  return <section id="agendar" className="w-full bg-panelAlt py-20 lg:py-28" data-id="element-59" data-mp="1d53tliu7xzo2">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-60" data-mp="pfj8ataleqbm">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-61" data-mp="pdyc6z8mqzbn">
          <div data-id="element-62" data-mp="kgy6wqjlu0ll">
            <p className="mb-4 text-xs uppercase tracking-[0.28em] text-gold" data-id="element-63" data-mp="kfdnnx2qq431">{t.booking.eyebrow}</p>
            <h2 className="font-display text-3xl font-bold uppercase leading-tight text-cream sm:text-4xl lg:text-[2.9rem]" data-id="element-64" data-mp="2khmxnkpoe6lz">
              {t.booking.title}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cream/75 lg:text-lg" data-id="element-65" data-mp="2u9sogr46yhzl">
              {t.booking.intro}
            </p>
            <div className="mt-8 space-y-3" data-id="element-66" data-mp="21klf0x3787zk">
              <a href={business.phoneHref} className="flex items-center gap-3 rounded-sm border border-gold/30 bg-ink px-5 py-4 transition-colors duration-150 ease-out hover:border-gold" data-id="element-67" data-mp="21ln2y25bcoyp">
                <PhoneIcon className="h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-68" data-mp="2ane9f97ogztm" />
                <span data-id="element-69" data-mp="2flo31fdr7sd4">
                  <span className="block text-xs uppercase tracking-widest text-cream/55" data-id="element-70" data-mp="2fi0bglrvdg11">
                    {t.booking.callLabel}
                  </span>
                  <span className="font-display text-lg text-cream" data-id="element-71" data-mp="3qy8cujvkelb4">{business.phoneDisplay}</span>
                </span>
              </a>
              <a href={business.whatsappHref} target="_blank" rel="noreferrer noopener" className="flex items-center gap-3 rounded-sm border border-white/10 bg-ink px-5 py-4 transition-colors duration-150 ease-out hover:border-gold/50" data-id="element-72" data-mp="4zwy27e2fv34">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold text-[10px] font-bold text-ink" aria-hidden="true" data-id="element-73" data-mp="3icp9v83srjjy">
                  W
                </span>
                <span data-id="element-74" data-mp="38fb3607oe94u">
                  <span className="block text-xs uppercase tracking-widest text-cream/55" data-id="element-75" data-mp="38gm8i4grroy2">
                    {t.booking.whatsappLabel}
                  </span>
                  <span className="font-display text-lg text-cream" data-id="element-76" data-mp="16m4uc3mhbv4h">
                    {t.booking.whatsappAction}
                  </span>
                </span>
              </a>
            </div>
            {barberName && <div className="mt-6 flex items-center gap-3 rounded-sm border border-gold/40 bg-ink px-5 py-4" data-id="element-77" data-mp="19my681r2i9f0">
                <img src={barbers[barberId!].image} alt="" className="h-12 w-12 shrink-0 rounded-sm bg-black object-cover" data-id="element-78" data-mp="1elrkvxi3c6gr" />
                <span className="flex-1" data-id="element-79" data-mp="14rsnfbsypav5">
                  <span className="block text-xs uppercase tracking-widest text-cream/55" data-id="element-80" data-mp="qy9nnmg24g6e">
                    {t.booking.labels.barber}
                  </span>
                  <span className="font-display text-lg uppercase tracking-wide text-gold" data-id="element-81" data-mp="vr2r86zshigw">
                    {barberName}
                  </span>
                </span>
                <button type="button" onClick={() => setBarberId(null)} className="text-xs uppercase tracking-widest text-cream/55 transition-colors duration-150 ease-out hover:text-gold-light" data-id="element-82" data-mp="lzq2ho9af8f3">
                  {t.barbers.clear}
                </button>
              </div>}
            <p className="mt-6 text-sm leading-relaxed text-cream/55" data-id="element-83" data-mp="3kk2uv2u9c25k">{t.booking.walkIn}</p>
          </div>

          <div className="rounded-sm border border-white/10 bg-ink p-6 sm:p-9" data-id="element-84" data-mp="2epupg4e2ssl">
            {status === 'sent' ? <div role="status" aria-live="polite" className="flex min-h-[420px] flex-col items-center justify-center text-center" data-id="element-85" data-mp="3ugnxjhit1168">
                <CheckCircle2Icon className="h-14 w-14 text-gold" aria-hidden="true" data-id="element-86" data-mp="7byf7nd0f8x0" />
                <h3 className="mt-6 font-display text-2xl font-bold uppercase text-cream" data-id="element-87" data-mp="m0m7nkrk72iq">
                  {t.booking.success.title}
                </h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-cream/75" data-id="element-88" data-mp="v2bxwn9pfvex">
                  {t.booking.success.thanks},{' '}
                  {form.name.split(' ')[0] || t.booking.success.neighbor}.{' '}
                  {t.booking.success.confirmA} {form.phone} {t.booking.success.confirmB}{' '}
                  {form.date || t.booking.success.chosenDay} · {form.time}
                  {barberName ? ` ${t.booking.success.with} ${barberName}` : ''}.
                </p>
                <button type="button" onClick={() => {
              setForm(emptyForm);
              setStatus('idle');
            }} className="mt-8 rounded-sm border border-gold/40 px-6 py-3 font-display text-sm font-semibold uppercase tracking-wider text-gold transition-colors duration-150 ease-out hover:bg-gold hover:text-ink" data-id="element-89" data-mp="3su2jbno0bi7w">
                  {t.booking.success.again}
                </button>
              </div> : <form onSubmit={handleSubmit} data-id="element-90" data-mp="2i6dceokyg8p8">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-91" data-mp="2i7g6f5dkqr9d">
                  <div className="sm:col-span-2" data-id="element-92" data-mp="2m6ow9z5hu4zn">
                    <label className={labelClass} htmlFor="name" data-id="element-93" data-mp="2m9jgpqoibpt2">
                      {t.booking.labels.name}
                    </label>
                    <input id="name" name="name" type="text" required autoComplete="name" value={form.name} onChange={update('name')} placeholder={t.booking.placeholders.name} className={fieldClass} data-id="element-94" data-mp="29wpfyo8wxvds" />
                  </div>
                  <div data-id="element-95" data-mp="2so8iyv2xt9ft">
                    <label className={labelClass} htmlFor="phone" data-id="element-96" data-mp="2slw4irwe1h7i">
                      {t.booking.labels.phone}
                    </label>
                    <input id="phone" name="phone" type="tel" required autoComplete="tel" value={form.phone} onChange={update('phone')} placeholder={t.booking.placeholders.phone} className={fieldClass} data-id="element-97" data-mp="2jntvbp2i3i95" />
                  </div>
                  <div data-id="element-98" data-mp="3c9lxtr6kdbqr">
                    <label className={labelClass} htmlFor="barber" data-id="element-99" data-mp="2ybxggtbpgmqg">
                      {t.booking.labels.barber}
                    </label>
                    <select id="barber" name="barber" value={barberId ?? ''} onChange={event => setBarberId(event.target.value === '' ? null : event.target.value as typeof barberOrder[number])} className={fieldClass} data-id="element-100" data-mp="33bthqkt0q02b">
                      <option value="" data-id="element-101" data-mp="dsi33g7924v8">{t.booking.anyBarber}</option>
                      {barberOrder.map(id => <option key={id} value={id} data-id="element-102" data-mp="imw2dtp18w56">
                          {barbers[id].name}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-103" data-mp="1yaaqed9zvipj">
                    <label className={labelClass} htmlFor="service" data-id="element-104" data-mp="2c3a2e5akiusc">
                      {t.booking.labels.service}
                    </label>
                    <select id="service" name="service" value={serviceValue} onChange={update('service')} className={fieldClass} data-id="element-105" data-mp="282ggfhgorq23">
                      {t.booking.serviceOptions.map(option => <option key={option} value={option} data-id="element-106" data-mp="1tao3td8a0q42">
                          {option}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-107" data-mp="2zt4hktid0vfr">
                    <label className={labelClass} htmlFor="date" data-id="element-108" data-mp="2ztmb3idn0vjz">
                      {t.booking.labels.date}
                    </label>
                    <input id="date" name="date" type="date" required min={today} value={form.date} onChange={update('date')} className={fieldClass} data-id="element-109" data-mp="11e16mzzf0f43" />
                  </div>
                  <div data-id="element-110" data-mp="iobdsif7q3et">
                    <label className={labelClass} htmlFor="time" data-id="element-111" data-mp="ipmhyjckld6g">
                      {t.booking.labels.time}
                    </label>
                    <select id="time" name="time" value={form.time} onChange={update('time')} className={fieldClass} data-id="element-112" data-mp="8pvqkqjb3eal">
                      {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-113" data-mp="1z2balkirhcxu">
                          {slot}
                        </option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-114" data-mp="3lz138u6hxexa">
                    <label className={labelClass} htmlFor="notes" data-id="element-115" data-mp="3lxpyar1be14l">
                      {t.booking.labels.notes}
                    </label>
                    <textarea id="notes" name="notes" rows={3} value={form.notes} onChange={update('notes')} placeholder={t.booking.placeholders.notes} className={`${fieldClass} resize-none`} data-id="element-116" data-mp="3urbf0fx1hsgn" />
                  </div>
                </div>

                <button type="submit" disabled={status === 'sending'} className="mt-7 flex w-full items-center justify-center gap-2 rounded-sm bg-gold px-6 py-4 font-display text-base font-semibold uppercase tracking-wider text-ink transition-colors duration-150 ease-out hover:bg-gold-light disabled:opacity-70" data-id="element-117" data-mp="3c0legxuxyhqy">
                  {status === 'sending' ? <>
                      <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-118" data-mp="2jbvyz66jsnnr" />
                      {t.booking.sending}
                    </> : <>
                      <CalendarCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-119" data-mp="2sdmz7vibk2a8" />
                      {t.booking.submit}
                    </>}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-cream/50" data-id="element-120" data-mp="1zpr8r8jcf6lw">
                  {t.booking.disclaimer}
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}